import * as Sentry from '@sentry/react';
import { useEffect, useMemo, useState } from 'react';
import { Alert, AlertDescription, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Textarea } from '@ludolio/shared-ui';
import { Activity as ActivityIcon, Archive, BookOpen, Copy, LibraryBig, Plus, Search, Share2, Users, X } from 'lucide-react';
import { apiClient } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import type { ActivityLibraryItem, ActivityLibraryVisibility, Achievement, Game, Unit } from '@/types';

type QuestionDraft = { question: string; options: string[]; correctAnswers: string[] };

const initialQuestions: QuestionDraft[] = [{ question: '', options: ['', ''], correctAnswers: [] }];
const teacherName = (item: ActivityLibraryItem) => [item.creator?.firstName, item.creator?.lastName].filter(Boolean).join(' ') || item.creator?.email || 'Unknown teacher';
const visibilityLabel: Record<ActivityLibraryVisibility, string> = { private: 'Private', org: 'Shared with org', global: 'Global' };

export function ActivityLibraryView() {
  const currentUser = useAuthStore((state) => state.user);
  const [libraryItems, setLibraryItems] = useState<ActivityLibraryItem[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ActivityLibraryItem | null>(null);
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  const [gameId, setGameId] = useState('');
  const [achievementId, setAchievementId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'quiz' | 'short_answer'>('short_answer');
  const [visibility, setVisibility] = useState<ActivityLibraryVisibility>('private');
  const [questions, setQuestions] = useState<QuestionDraft[]>(initialQuestions);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [libraryResponse, unitsResponse, gamesResponse] = await Promise.all([
        apiClient.listActivityLibrary(),
        apiClient.getMyUnits(),
        apiClient.getAllGames(),
      ]);
      if (!libraryResponse.success) throw new Error(libraryResponse.error || 'Failed to load activity library');
      if (!unitsResponse.success) throw new Error(unitsResponse.error || 'Failed to load units');
      if (!gamesResponse.success) throw new Error(gamesResponse.error || 'Failed to load games');
      setLibraryItems(libraryResponse.data || []);
      setUnits(unitsResponse.data || []);
      setGames(gamesResponse.data || []);
    } catch (err) {
      console.error('Failed to load activity library:', err);
      Sentry.captureException(err);
      setError(err instanceof Error ? err.message : 'Failed to load activity library');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return libraryItems;
    return libraryItems.filter((item) => [item.title, item.description, item.game?.title, item.achievement?.name, teacherName(item), visibilityLabel[item.visibility]].some((value) => value?.toLowerCase().includes(query)));
  }, [libraryItems, search]);

  const stats = useMemo(() => ({
    total: libraryItems.length,
    shared: libraryItems.filter((item) => item.visibility === 'org').length,
    teachers: new Set(libraryItems.map((item) => item.creator?.id || item.createdBy)).size,
    games: new Set(libraryItems.map((item) => item.gameId)).size,
  }), [libraryItems]);

  const resetForm = () => {
    setGameId('');
    setAchievementId('');
    setTitle('');
    setDescription('');
    setType('short_answer');
    setVisibility('private');
    setQuestions(initialQuestions);
    setAchievements([]);
  };

  const handleGameChange = async (nextGameId: string) => {
    setGameId(nextGameId);
    setAchievementId('');
    try {
      const response = await apiClient.getGameAchievements(nextGameId);
      setAchievements(response.success ? response.data || [] : []);
    } catch (err) {
      console.error('Failed to load achievements:', err);
      Sentry.captureException(err);
      setAchievements([]);
    }
  };

  const buildQuestions = () => questions.map((question) => {
    const base: { question: string; options?: string[]; correctAnswers?: string[] } = { question: question.question.trim() };
    if (type === 'quiz') {
      base.options = question.options.map((option) => option.trim()).filter(Boolean);
      const validOptions = new Set(base.options);
      base.correctAnswers = question.correctAnswers.filter((answer) => validOptions.has(answer));
    }
    return base;
  });

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError(null);
      const response = await apiClient.createActivityLibraryItem({
        gameId,
        achievementId,
        title,
        description: description || undefined,
        type,
        visibility,
        config: { questions: buildQuestions() },
      });
      if (!response.success) throw new Error(response.error || 'Failed to create library item');
      setCreateOpen(false);
      resetForm();
      setNotice(`Created “${title}” in the activity library.`);
      await loadData();
    } catch (err) {
      console.error('Failed to create activity library item:', err);
      Sentry.captureException(err);
      setError(err instanceof Error ? err.message : 'Failed to create activity library item');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (item: ActivityLibraryItem) => {
    if (!confirm(`Archive “${item.title}”? Existing unit copies will not be affected.`)) return;
    try {
      const response = await apiClient.archiveActivityLibraryItem(item.id);
      if (!response.success) throw new Error(response.error || 'Failed to archive library item');
      setNotice(`Archived “${item.title}”.`);
      await loadData();
    } catch (err) {
      console.error('Failed to archive library item:', err);
      Sentry.captureException(err);
      setError(err instanceof Error ? err.message : 'Failed to archive library item');
    }
  };

  const handleImport = async () => {
    if (!selectedItem || !selectedUnitId) return;
    try {
      setImporting(true);
      setError(null);
      const response = await apiClient.importActivityToUnit(selectedItem.id, selectedUnitId);
      if (!response.success) throw new Error(response.error || 'Failed to copy activity into unit');
      const targetUnit = units.find((unit) => unit.id === selectedUnitId);
      setNotice(`Copied “${selectedItem.title}” into ${targetUnit?.code || 'the selected unit'}.`);
      setSelectedItem(null);
      setSelectedUnitId('');
    } catch (err) {
      console.error('Failed to import activity:', err);
      Sentry.captureException(err);
      setError(err instanceof Error ? err.message : 'Failed to copy activity into unit');
    } finally {
      setImporting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" /><p className="mt-4 text-muted-foreground">Loading activity library...</p></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3"><LibraryBig className="w-8 h-8 text-primary" />Activity Library</h1>
          <p className="text-muted-foreground mt-2">Create reusable activity templates, keep them private, or share them with teachers in your organization.</p>
        </div>
        <Dialog open={createOpen} onOpenChange={(open) => { setCreateOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild><Button><Plus className="w-4 h-4 mr-2" />Create Library Activity</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Create Library Activity</DialogTitle><DialogDescription>This creates a reusable template. Teachers copy templates into units as independent activity instances.</DialogDescription></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2"><Label>Game</Label><Select value={gameId} onValueChange={handleGameChange}><SelectTrigger><SelectValue placeholder="Select a game" /></SelectTrigger><SelectContent>{games.map((game) => <SelectItem key={game.id} value={game.id}>{game.title}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-2"><Label>Achievement trigger</Label><Select value={achievementId} onValueChange={setAchievementId} disabled={!gameId}><SelectTrigger><SelectValue placeholder="Select an achievement" /></SelectTrigger><SelectContent>{achievements.map((achievement) => <SelectItem key={achievement.id} value={achievement.id}>{achievement.name}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2"><Label>Activity type</Label><Select value={type} onValueChange={(value) => setType(value as 'quiz' | 'short_answer')}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="short_answer">Short Answer</SelectItem><SelectItem value="quiz">Quiz</SelectItem></SelectContent></Select></div>
                <div className="space-y-2"><Label>Sharing</Label><Select value={visibility} onValueChange={(value) => setVisibility(value as ActivityLibraryVisibility)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="private">Private</SelectItem><SelectItem value="org">Shared with org</SelectItem></SelectContent></Select></div>
              </div>
              <div className="space-y-2"><Label>Title</Label><Input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Activity title" required /></div>
              <div className="space-y-2"><Label>Description</Label><Textarea value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Briefly explain when teachers should use this activity" /></div>
              <div className="space-y-3">
                <div className="flex items-center justify-between"><Label>Questions</Label><Button type="button" variant="outline" size="sm" onClick={() => setQuestions((prev) => [...prev, { question: '', options: ['', ''], correctAnswers: [] }])}><Plus className="w-4 h-4 mr-1" />Add Question</Button></div>
                {questions.map((question, questionIndex) => (
                  <div key={questionIndex} className="rounded-md border p-3 space-y-3">
                    <div className="flex items-center justify-between"><span className="text-sm font-medium">Question {questionIndex + 1}</span>{questions.length > 1 && <Button type="button" variant="ghost" size="sm" onClick={() => setQuestions((prev) => prev.filter((_, index) => index !== questionIndex))}><X className="w-4 h-4" /></Button>}</div>
                    <Textarea value={question.question} onChange={(event) => setQuestions((prev) => prev.map((item, index) => index === questionIndex ? { ...item, question: event.target.value } : item))} placeholder="Enter your question" required />
                    {type === 'quiz' && <div className="space-y-2">{question.options.map((option, optionIndex) => <div key={optionIndex} className="flex gap-2"><Input value={option} onChange={(event) => setQuestions((prev) => prev.map((item, index) => { if (index !== questionIndex) return item; const nextOptions = [...item.options]; nextOptions[optionIndex] = event.target.value; return { ...item, options: nextOptions }; }))} placeholder={`Option ${optionIndex + 1}`} /><Button type="button" variant={question.correctAnswers.includes(option.trim()) ? 'default' : 'outline'} size="sm" disabled={!option.trim()} onClick={() => setQuestions((prev) => prev.map((item, index) => index === questionIndex ? { ...item, correctAnswers: item.correctAnswers.includes(option.trim()) ? item.correctAnswers.filter((answer) => answer !== option.trim()) : [...item.correctAnswers, option.trim()] } : item))}>Correct</Button></div>)}<Button type="button" variant="outline" size="sm" onClick={() => setQuestions((prev) => prev.map((item, index) => index === questionIndex ? { ...item, options: [...item.options, ''] } : item))}><Plus className="w-4 h-4 mr-1" />Add Option</Button></div>}
                  </div>
                ))}
              </div>
              <Button type="submit" className="w-full" disabled={saving || !gameId || !achievementId || !title || questions.some((question) => !question.question.trim())}>{saving ? 'Creating...' : 'Create Library Activity'}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
      {notice && <Alert><AlertDescription>{notice}</AlertDescription></Alert>}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Templates</CardTitle><ActivityIcon className="h-4 w-4 text-muted-foreground" /></CardHeader><CardContent><div className="text-2xl font-bold">{stats.total}</div><p className="text-xs text-muted-foreground">Persistent reusable activities</p></CardContent></Card>
        <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Org Shared</CardTitle><Share2 className="h-4 w-4 text-muted-foreground" /></CardHeader><CardContent><div className="text-2xl font-bold">{stats.shared}</div><p className="text-xs text-muted-foreground">Visible to teachers in your org</p></CardContent></Card>
        <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Teachers</CardTitle><Users className="h-4 w-4 text-muted-foreground" /></CardHeader><CardContent><div className="text-2xl font-bold">{stats.teachers}</div><p className="text-xs text-muted-foreground">Contributing templates</p></CardContent></Card>
        <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Games</CardTitle><BookOpen className="h-4 w-4 text-muted-foreground" /></CardHeader><CardContent><div className="text-2xl font-bold">{stats.games}</div><p className="text-xs text-muted-foreground">Covered by templates</p></CardContent></Card>
      </div>

      <div className="relative"><Search className="absolute left-3 top-1/2 w-4 h-4 -translate-y-1/2 text-muted-foreground" /><Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search title, game, achievement, teacher, or visibility..." className="pl-9" /></div>

      {filteredItems.length === 0 ? <Card><CardContent className="py-12 text-center"><LibraryBig className="w-16 h-16 text-muted-foreground mx-auto mb-4" /><h3 className="text-lg font-semibold mb-2">No library activities yet</h3><p className="text-muted-foreground">Create a reusable activity template to get started.</p></CardContent></Card> : (
        <div className="grid items-stretch gap-4 xl:grid-cols-2">{filteredItems.map((item) => {
          const isOwner = currentUser?.id === item.createdBy;
          const questionCount = item.config?.questions?.length || 0;

          return (
            <Card key={item.id} className="flex h-full flex-col">
              <CardHeader className="min-h-[7.25rem] pb-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1 space-y-2">
                    <CardTitle className="text-lg leading-tight">{item.title}</CardTitle>
                    <CardDescription className="line-clamp-2 min-h-[2.5rem] leading-5">{item.description || 'No description provided.'}</CardDescription>
                  </div>
                  <Badge variant={item.visibility === 'org' ? 'default' : 'secondary'} className="shrink-0 whitespace-nowrap rounded-full px-3 py-1 text-xs">
                    {visibilityLabel[item.visibility]}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col gap-4 pt-0">
                <div className="flex min-h-8 flex-wrap content-start gap-2">
                  <Badge variant="outline">{item.game?.title || 'Unknown game'}</Badge>
                  <Badge variant="outline">{item.achievement?.name || 'Unknown achievement'}</Badge>
                  <Badge variant="outline">{item.type === 'quiz' ? 'Quiz' : 'Short Answer'}</Badge>
                  <Badge variant="outline">{questionCount} question{questionCount === 1 ? '' : 's'}</Badge>
                </div>
                <div className="min-h-[4.25rem] rounded-lg border p-3 text-sm">
                  <p className="text-muted-foreground">Created by</p>
                  <p className="mt-1 font-medium">{teacherName(item)}</p>
                </div>
                <div className="min-h-[5.75rem] rounded-lg border p-3">
                  <p className="mb-2 text-sm font-medium">Preview</p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {(item.config?.questions || []).slice(0, 2).map((question, index) => <li key={`${item.id}-${index}`}>• {question.question}</li>)}
                    {questionCount > 2 && <li>• +{questionCount - 2} more questions</li>}
                  </ul>
                </div>
                <div className="mt-auto flex flex-wrap justify-end gap-2 pt-1">
                  {isOwner && <Button variant="outline" onClick={() => handleArchive(item)}><Archive className="w-4 h-4 mr-2" />Archive</Button>}
                  <Button onClick={() => { setSelectedItem(item); setSelectedUnitId(units[0]?.id || ''); }} disabled={units.length === 0}><Copy className="w-4 h-4 mr-2" />Copy to Unit</Button>
                </div>
              </CardContent>
            </Card>
          );
        })}</div>
      )}

      <Dialog open={Boolean(selectedItem)} onOpenChange={(open) => !open && setSelectedItem(null)}><DialogContent><DialogHeader><DialogTitle>Copy to Unit</DialogTitle><DialogDescription>Copies this template into a unit as an independent activity. The unit must already include {selectedItem?.game?.title || 'the required game'}.</DialogDescription></DialogHeader><div className="space-y-4"><div className="space-y-2"><Label>Template</Label><div className="rounded-md border px-3 py-2 text-sm">{selectedItem?.title || 'No template selected'}</div></div><div className="space-y-2"><Label>Target unit</Label><Select value={selectedUnitId} onValueChange={setSelectedUnitId}><SelectTrigger><SelectValue placeholder="Select a unit" /></SelectTrigger><SelectContent>{units.map((unit) => <SelectItem key={unit.id} value={unit.id}>{unit.code} — {unit.name}</SelectItem>)}</SelectContent></Select></div><div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setSelectedItem(null)}>Cancel</Button><Button onClick={handleImport} disabled={!selectedUnitId || importing}>{importing ? 'Copying...' : 'Copy Activity'}</Button></div></div></DialogContent></Dialog>
    </div>
  );
}